import { memo, useState } from "react";
import {
  Calculator,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FlaskConical,
  Globe,
  GraduationCap,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";

import type { SubagentKind, SubagentResultPayload } from "../../domain/types";
import { Pill } from "../common/Pill";
import { cn } from "../common/cn";

interface SubagentResultCardProps {
  kind: SubagentKind;
  result: SubagentResultPayload;
  defaultOpen?: boolean;
}

/**
 * Kalite Planı — `subagent.result` akış kartı. ResearchSubcard ile
 * aynı görsel dil: glass collapsible section, başlıkta kind etiketi +
 * durum pill'i, gövdede özet + kanıt linkleri; `compute` türünde
 * stdout monospace blok + outputHash rozeti.
 *
 * Event hiç gelmezse bu kart transcript akışında hiç görünmez
 * (RealTimeTranscript feed'i sadece store'daki kayıtları render eder).
 */
const KIND_META: Record<
  SubagentKind,
  { label: string; Icon: LucideIcon; accent: string }
> = {
  micro_expert: {
    label: "Mikro Uzman",
    Icon: GraduationCap,
    accent: "text-violet-300",
  },
  research: { label: "Araştırma Alt-Ajanı", Icon: Globe, accent: "text-sky-300" },
  verifier: { label: "Doğrulayıcı", Icon: ShieldCheck, accent: "text-emerald-300" },
  compute: { label: "Hesaplama", Icon: Calculator, accent: "text-amber-300" },
};

function safeHref(url: string): string {
  if (/^https?:\/\//i.test(url) || url.startsWith("//")) return url;
  return `https://${url.replace(/^\/+/, "")}`;
}

function hostFromUrl(url: string): string {
  try {
    return new URL(safeHref(url)).host;
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0] ?? url;
  }
}

export const SubagentResultCard = memo(function SubagentResultCard({
  kind,
  result,
  defaultOpen = false,
}: SubagentResultCardProps) {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const reduceMotion = useReducedMotion();
  const meta = KIND_META[kind];
  const KindIcon = meta.Icon;
  const confidencePct = Math.round(Math.max(0, Math.min(1, result.confidence)) * 100);

  return (
    <section
      className={cn(
        "glass relative flex min-w-0 flex-col gap-1.5 overflow-hidden rounded-lg border border-white/[0.06] px-3 py-2.5",
        "bg-[radial-gradient(circle_at_25%_-20%,hsl(260_70%_70%/0.08),transparent_60%)]",
      )}
      aria-label={`Alt-ajan sonucu: ${meta.label}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="focus-ring flex items-center justify-between gap-3 rounded-md text-left"
        aria-expanded={open}
      >
        <span className="flex min-w-0 flex-wrap items-center gap-2 text-xs font-medium text-fg-muted">
          <KindIcon className={cn("h-3.5 w-3.5", meta.accent)} strokeWidth={1.5} aria-hidden />
          <span className="shrink-0">{meta.label}</span>
          {result.kind && result.kind !== kind ? (
            <span className="truncate font-mono text-[10px] text-fg-subtle">
              ({result.kind})
            </span>
          ) : null}
          {result.ok ? (
            <Pill tone="success" className="text-[10px] uppercase tracking-wider">
              Tamamlandı
            </Pill>
          ) : (
            <Pill
              tone="danger"
              className="text-[10px] uppercase tracking-wider"
              icon={<XCircle className="h-3 w-3" aria-hidden />}
            >
              Hata
            </Pill>
          )}
          {confidencePct > 0 ? (
            <span className="font-mono text-[10px] tabular-nums text-fg-subtle">
              güven %{confidencePct}
            </span>
          ) : null}
        </span>
        <span className="text-fg-subtle">
          {open ? (
            <ChevronDown className="h-4 w-4" strokeWidth={1.5} aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} aria-hidden />
          )}
        </span>
      </button>

      {result.summary ? (
        <p className={cn("text-xs leading-snug text-fg-muted", !open && "line-clamp-2")}>
          {result.summary}
        </p>
      ) : null}

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="body"
            initial={reduceMotion ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="flex flex-col gap-2 overflow-hidden"
          >
            {result.error ? (
              <p className="flex items-start gap-1.5 rounded-md border border-rose-500/25 bg-rose-500/[0.06] px-2 py-1.5 text-[11px] leading-snug text-rose-200">
                <XCircle className="mt-0.5 h-3 w-3 flex-none" aria-hidden />
                <span className="break-words">{result.error}</span>
              </p>
            ) : null}

            {result.evidenceUrls.length > 0 ? (
              <ul className="flex flex-col gap-0.5">
                {result.evidenceUrls.map((url, i) => (
                  <li key={`${url}-${i}`} className="min-w-0">
                    <a
                      href={safeHref(url)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="focus-ring inline-flex max-w-full items-center gap-1.5 rounded-sm text-[11px] text-accent-300 underline-offset-4 hover:underline"
                    >
                      <ExternalLink className="h-3 w-3 flex-none text-fg-subtle" aria-hidden />
                      <span className="truncate" title={url}>
                        {hostFromUrl(url)}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}

            {result.computation ? (
              <div className="flex min-w-0 flex-col gap-1.5">
                {result.computation.stdout ? (
                  <pre className="max-h-[14rem] overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-surface-0/60 p-2 font-mono text-[11px] leading-relaxed text-fg-muted ring-1 ring-white/[0.05]">
                    {result.computation.stdout}
                  </pre>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  {result.computation.outputHash ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-amber-400/25 bg-amber-500/[0.08] px-2 py-0.5 font-mono text-[10px] tabular-nums text-amber-200/90"
                      title="Deterministik çıktı özeti — aynı kod + girdi her zaman aynı hash'i üretir."
                    >
                      <FlaskConical className="h-3 w-3" aria-hidden />
                      {result.computation.outputHash}
                    </span>
                  ) : null}
                </div>
                {result.computation.code ? (
                  <details className="group rounded-md border border-white/[0.05] bg-surface-0/40 px-2 py-1.5">
                    <summary className="cursor-pointer list-none select-none text-[10px] font-medium uppercase tracking-wider text-fg-subtle transition group-open:text-fg-muted">
                      Çalıştırılan kod
                    </summary>
                    <pre className="mt-1.5 max-h-[12rem] overflow-y-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-fg-muted">
                      {result.computation.code}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  );
});
